import { Col, Descriptions, Empty, Form, Row, Space, Typography } from 'antd';
import { get, last } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Trans, withNamespaces } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';

import { impersonateUser } from '../../api/auth';
import { handleApiErrors } from '../../api/axiosInstance';
import AxleAPI from '../../api/axle';
import { FAVR_COMPLIANCES_API } from '../../api/favr-compliances';
import { fetchCurrentUserDetails } from '../../api/user';
import { Button } from '../../components';
import Box from '../../components/Box';
import GenericModal from '../../components/Modal/GenericModal';
import TripsCommentsModal from '../../components/Modal/TripsCommentsModal';
import UploadedFilePreviewModal from '../../components/Modal/UploadedFilePreviewModal';
import PageBreadcrumbs from '../../components/PageBreadcrumbs';
import SpaceSpinner from '../../components/SpaceSpinner';
import ComplianceAuditLogTable from '../../components/Table/ComplianceAuditLogTable';
import UploadedFilesTable from '../../components/Table/UploadedFilesTable';
import StatusTag from '../../components/Tag/StatusTag';
import Text from '../../components/Text';
import Toast from '../../components/Toast';
import AxleConnectionStatus from '../../components/UserInsuranceSettingFormContent/AxleConnectionStatus';
import PageContainer from '../../containers/PageContainer';
import { INTERNAL_LINKS, STATUS_LIST } from '../../enum';
import { VEHICLE_INSURANCE_PROVIDERS } from '../../enum/VehicleInsuranceProviders';
import withAuthentication from '../../hocs/withAuthentication';
import useDidUpdateEffect from '../../hooks/useDidUpdateEffect';
import useModal from '../../hooks/useModal';
import useTablePagination from '../../hooks/useTablePagination';
import { updateProfile } from '../../stores/actions/profile';
import {
  formatPageTitle,
  getUserFullName,
  setFullscreenMaxHeight,
  setModalFullscreenWidth,
} from '../../utils/common';
import { getMomentDateWithoutTimezone } from '../../utils/datetime';
import { normalizeVehicleInsuranceInformation } from '../../utils/insurancePolicies';
import { setObjectValuesToBoolean } from '../../utils/object';
import { formatAddressIntoString } from '../../utils/trips';
import { normalizeUserSchema } from '../../utils/users';
import { getEnvironmentName } from '../../utils/window';

const RegularInsuranceWorklistForm = React.lazy(() =>
  import('./components/RegularInsuranceWorklistForm'),
);

const AxleInsuranceWorklistComparison = React.lazy(() =>
  import('./components/AxleInsuranceWorklistComparison'),
);

const PageHelmet = () => (
  <Helmet>
    <title>{formatPageTitle('Compliance Worklist Details')}</title>
  </Helmet>
);

const ComplianceWorklistDetails = props => {
  const { t, history, match } = props;

  const dispatch = useDispatch();

  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [initialValuesSet, setInitialValuesSet] = useState(false);
  const [vehicleData, setVehicleData] = useState({});
  const [isOpeningAxleReport, setIsOpeningAxleReport] = useState(false);
  const [axleReport, setAxleReport] = useState();
  const [uploadedFileToView, setUploadedFileToView] = useState();
  const [
    isRemarksForUserModalVisible,
    openRemarksForUserModal,
    closeRemarksForUserModal,
  ] = useModal();
  const [
    isRemarksForKliksAdminModalVisible,
    openRemarksForKliksAdminModal,
    closeRemarksForKliksAdminModal,
  ] = useModal();

  const handleVehicleDataChange = updatedValues =>
    setVehicleData(state => ({ ...state, ...updatedValues }));

  const complianceDetailsNormalizer = useCallback(data => {
    const normalizedData = { ...data };

    if (data?.vehicleInsurancePolicyId) {
      normalizedData.vehicleInsurancePolicyId = normalizeVehicleInsuranceInformation({
        ...data.vehicleInsurancePolicyId,
        uploadedFiles: data.uploadedFiles,
      });
    }

    return normalizedData;
  }, []);

  const complianceDetailsQuery = useQuery({
    queryKey: ['fetchFavrComplianceDetails', match.params.id],
    queryFn: () => FAVR_COMPLIANCES_API.fetchFavrComplianceDetails(match.params.id),
    select: complianceDetailsNormalizer,
    onError: error => {
      handleApiErrors(error.response, () => {
        Toast({
          type: 'error',
          message: t('fetchFavrComplianceDetailsError'),
        });
      });

      history.push(INTERNAL_LINKS.COMPLIANCE_WORKLIST);
    },
  });

  const complianceStatus = complianceDetailsQuery?.data?.status;
  const previousComplianceComponents = complianceDetailsQuery?.data?.previousComplianceComponents;
  const currentComplianceComponents = complianceDetailsQuery?.data?.complianceComponents;
  const vehicleInsurance = complianceDetailsQuery?.data?.vehicleInsurancePolicyId;
  const insuranceProvider = vehicleInsurance?.provider;
  const isAxleInsurance = insuranceProvider === VEHICLE_INSURANCE_PROVIDERS.AXLE;
  const isManualInsurance = insuranceProvider === VEHICLE_INSURANCE_PROVIDERS.MANUAL;
  const hasVehicleInsurance = !!vehicleInsurance;

  const handleOpenAxlePolicyReport = useCallback(async () => {
    const externalPolicyId = vehicleInsurance?.externalPolicyId;
    if (!externalPolicyId || isOpeningAxleReport) return;

    setIsOpeningAxleReport(true);

    try {
      const report = await AxleAPI.fetchAxleInsuranceDocumentPreview(externalPolicyId);
      setAxleReport(report);
    } catch (error) {
      handleApiErrors(error.response, () => {
        Toast({
          type: 'error',
          message: t('fetchAxleInsuranceDocumentPreviewError'),
        });
      });
    } finally {
      setIsOpeningAxleReport(false);
    }
  }, [isOpeningAxleReport, t, vehicleInsurance]);

  const closeAxlePolicyReport = () => {
    if (axleReport?.url) {
      URL.revokeObjectURL(axleReport.url);
    }
    setAxleReport();
  };

  useEffect(
    () => () => {
      if (axleReport?.url) {
        URL.revokeObjectURL(axleReport.url);
      }
    },
    [axleReport],
  );

  const isAxleReportImage = axleReport?.type?.startsWith('image/');
  const hasAxleReportDimensions = axleReport?.width > 0 && axleReport?.height > 0;
  const axleReportModalHeight = setFullscreenMaxHeight(1400);
  const axleReportModalWidth =
    isAxleReportImage && hasAxleReportDimensions
      ? Math.min(
          setModalFullscreenWidth(),
          Math.max(400, axleReportModalHeight * (axleReport.width / axleReport.height)),
        )
      : setModalFullscreenWidth();

  const handleOpenAxleDashboard = useCallback(() => {
    const policyId = vehicleInsurance?.externalPolicyId;
    const clientId = get(
      vehicleInsurance,
      'source.externalAccountId.axleUserMappingId.axleClientId.destinationClientId',
    );

    if (!policyId || !clientId) return;

    const axleDashboardBaseUrl =
      getEnvironmentName() === 'prod'
        ? 'https://dashboard.axle.insure/policies'
        : 'https://dashboard.sandbox.axle.insure/policies';

    window.open(
      `${axleDashboardBaseUrl}/${policyId}?client=${clientId}`,
      '_blank',
      'noopener,noreferrer',
    );
  }, [vehicleInsurance]);

  const {
    paginationConfig,
    handleTotalUpdate,
    handlePageChange,
    handlePageSizeChange,
  } = useTablePagination();
  const userComplianceHistoryQuery = useQuery({
    enabled: !!complianceDetailsQuery?.data?.userId?._id,
    initialData: { documents: [], totalCount: 0 },
    queryKey: [
      'fetchUserFavrComplianceHistory',
      complianceDetailsQuery?.data?.userId?._id,
      match.params.id,
      paginationConfig.current,
      paginationConfig.pageSize,
    ],
    queryFn: () =>
      FAVR_COMPLIANCES_API.fetchUserFavrComplianceHistory(
        complianceDetailsQuery?.data?.userId?._id,
        paginationConfig.current,
        paginationConfig.pageSize,
        { favrComplianceId: match.params.id },
      ),
    onSuccess: ({ totalCount }) => handleTotalUpdate(totalCount),
  });

  const uploadedFiles = useMemo(
    () =>
      (vehicleInsurance?.uploadedFiles || [])
        .filter(file => file?._id)
        .map(file => ({
          ...file,
          uid: file._id,
          timestamp: file.createdAt,
        })),
    [vehicleInsurance],
  );

  const [isInsuranceCompliance, isBusinessVehicleCompliance] = useMemo(
    () => [
      complianceDetailsQuery.data?.type === 'insurance',
      complianceDetailsQuery.data?.type === 'business-vehicle',
    ],
    [complianceDetailsQuery],
  );

  useDidUpdateEffect(() => {
    if (!initialValuesSet && complianceDetailsQuery?.data?.userId) {
      setInitialValuesSet(true);

      const queryData = get(complianceDetailsQuery, 'data', {});
      const complianceComponentsValues = get(queryData, 'complianceComponentsValues', {});
      const complianceComponents = get(queryData, 'complianceComponents', {});
      const vehicleComplianceData = get(queryData, 'vehicleId') || {};
      const vehicleInsurancePolicyId = get(queryData, 'vehicleInsurancePolicyId', {});
      let initialValues = {};

      if (queryData.type === 'business-vehicle') {
        setVehicleData(vehicleComplianceData);
        initialValues = {
          depreciationCompliance: false,
          electronicSignatureCompliance: false,
          msrpCompliance: false,
          odometerReading: false,
          vehicleYearCompliance: false,

          ...(complianceComponents || {}),
          vehicleYear: vehicleComplianceData.year,
          vehicleMake: vehicleComplianceData.make,
          vehicleModel: vehicleComplianceData.model,
          odometer: last(vehicleComplianceData.odometer || [])?.mileage,
        };
      } else if (queryData.type === 'insurance') {
        initialValues = {
          bodilyInjuryByUnderinsuredAuto: false,
          bodilyInjuryByUninsuredAuto: false,
          bodilyInjuryToOthers: false,
          collision: false,
          comprehensive: false,
          damageToProperty: false,
          insuranceCompany: false,
          insuranceHomeAddress: false,
          insuranceName: false,
          insurancePolicyEndDate: false,
          insurancePolicyStartDate: false,
          insuranceVehicle: false,
          medicalPayments: false,
          optionalBodilyInjuryToOthers: false,
          personalInjuryProtection: false,
          stateMinimum: false,

          ...(complianceComponentsValues || {}),
          ...(complianceComponents || {}),
          driverNameOnPolicy: getUserFullName(queryData.userId),
          driverHomeAddressOnPolicy: queryData.userId?.personalInfor?.homeAddress
            ? formatAddressIntoString(queryData.userId.personalInfor.homeAddress)
            : undefined,
          vehicleYearMakeModelPolicy: [
            vehicleComplianceData.year,
            vehicleComplianceData.make,
            vehicleComplianceData.model,
          ]
            .join(' ')
            .trim(),
          insurancePolicyStartDateValue: vehicleInsurancePolicyId?.effectiveDate
            ? getMomentDateWithoutTimezone(vehicleInsurancePolicyId.effectiveDate)
            : undefined,
          insurancePolicyEndDateValue: vehicleInsurancePolicyId?.expirationDate
            ? getMomentDateWithoutTimezone(vehicleInsurancePolicyId.expirationDate)
            : undefined,
        };
      }

      form.setFieldsValue(initialValues);
    }
  }, [complianceDetailsQuery, initialValuesSet]);

  const updateFavrComplianceMutation = useMutation(
    values => FAVR_COMPLIANCES_API.updateFavrCompliance(match.params.id, values),
    {
      onSuccess: () => {
        Toast({
          type: 'open',
          message: t('updateFavrComplianceSuccess'),
        });
      },
    },
  );

  const overrideFavrComplianceComponentsMutation = useMutation(
    ({ reason, complianceComponents }) =>
      FAVR_COMPLIANCES_API.overrideMeasureOneComplianceStatus(
        match.params.id,
        reason,
        complianceComponents,
      ),
    {
      onSuccess: () => {
        Toast({
          type: 'open',
          message: t('overrideFavrComplianceSuccess'),
        });
      },
    },
  );

  const _updateComplianceItem = (newValues = {}) => {
    const queryKey = [FAVR_COMPLIANCES_API.fetchFavrComplianceDetails.name, match.params.id];
    let currentQueryData = queryClient.getQueryData(queryKey);
    queryClient.setQueryData(queryKey, { ...currentQueryData, ...newValues });
  };

  const addRemarksForUser = useMutation(
    note => FAVR_COMPLIANCES_API.addNoteToFavrCompliance(match.params.id, note, false),
    {
      onSuccess: updatedNotes => _updateComplianceItem({ notesForUser: updatedNotes }),
      onError: error => handleApiErrors(error.response),
    },
  );

  const addRemarksForInternalAdmin = useMutation(
    note => FAVR_COMPLIANCES_API.addNoteToFavrCompliance(match.params.id, note, true),
    {
      onSuccess: updatedNotes => _updateComplianceItem({ notes: updatedNotes }),
      onError: error => handleApiErrors(error.response),
    },
  );

  const handleSetStatus = async reasonForOverride => {
    const newComplianceComponents = setObjectValuesToBoolean(
      currentComplianceComponents,
      complianceStatus === STATUS_LIST().Status.DENIED ? true : false,
    );

    await overrideFavrComplianceComponentsMutation.mutateAsync({
      reason: reasonForOverride,
      complianceComponents: newComplianceComponents,
    });
  };

  const handleOverrideSubmit = () => {
    // Validate the form fields before submission
    form.validateFields().then(async formValues => {
      const {
        kliksAdminOverride: override,
        reasonForOverride,
        overrideType,
        ...complianceComponents
      } = formValues;

      try {
        if (override) {
          if (overrideType === 'revert' && previousComplianceComponents) {
            await overrideFavrComplianceComponentsMutation.mutateAsync({
              reason: reasonForOverride,
              complianceComponents: previousComplianceComponents,
            });
          }

          if (overrideType === 'status' && currentComplianceComponents) {
            await handleSetStatus(reasonForOverride);
          }

          if (overrideType === 'components' && complianceComponents) {
            await overrideFavrComplianceComponentsMutation.mutateAsync({
              reason: reasonForOverride,
              complianceComponents,
            });
          }

          complianceDetailsQuery.refetch();
        }

        userComplianceHistoryQuery.refetch();
        form.setFieldsValue({
          reasonForOverride: '',
          kliksAdminOverride: false,
        });
      } catch (error) {
        handleApiErrors(error.response);
      }
    });
  };

  // This function handles the form submission for compliance worklist details
  const handleSubmit = () => {
    // Validate the form fields before submission
    form.validateFields().then(async formValues => {
      const { kliksAdminOverride: override, reasonForOverride, ...values } = formValues;
      try {
        //Check if the compliance type is for insurance and update accordingly
        if (isInsuranceCompliance) {
          await updateFavrComplianceMutation.mutateAsync({
            ...values,
            insurancePolicyStartDateValue: values?.insurancePolicyStartDateValue
              ? getMomentDateWithoutTimezone(values?.insurancePolicyStartDateValue)
                  .utc(true)
                  .toISOString()
              : undefined,
            insurancePolicyEndDateValue: values?.insurancePolicyEndDateValue
              ? getMomentDateWithoutTimezone(values?.insurancePolicyEndDateValue)
                  .utc(true)
                  .toISOString()
              : undefined,
          });
          complianceDetailsQuery.refetch();
        }

        if (override) {
          await handleSetStatus(reasonForOverride);
        }

        userComplianceHistoryQuery.refetch();
      } catch (error) {
        handleApiErrors(error.response);
      }
    });
  };

  const complianceUserId = useMemo(() => {
    return get(complianceDetailsQuery.data, 'userId._id');
  }, [complianceDetailsQuery.data]);

  /**
   * Replaces the currently logged in User with a selected User
   */
  const handleImpersonation = useCallback(async () => {
    const result = await impersonateUser(complianceUserId);

    if (result) {
      const userResult = await fetchCurrentUserDetails();
      const normalizedUserSchema = normalizeUserSchema(userResult);
      return normalizedUserSchema;
    }
  }, [complianceUserId]);

  const impersonateMutation = useMutation(handleImpersonation, {
    onError: error => handleApiErrors(error.response),
    onSuccess: async userSchema => {
      if (userSchema) {
        dispatch(updateProfile(userSchema));

        Toast({
          type: 'warning',
          message: (
            <Trans
              t={t}
              i18nKey="youAreImpersonating_name"
              values={{ name: getUserFullName(userSchema) }}
              components={[<b>Dummy</b>]}
            />
          ),
        });
      }
    },
  });

  if (!initialValuesSet) {
    return <PageContainer loading>{<PageHelmet />}</PageContainer>;
  }

  return (
    <PageContainer
      title={
        <PageBreadcrumbs
          items={
            complianceDetailsQuery.isFetching
              ? []
              : [
                  {
                    label: t('compliance'),
                    onClick: () => history.push(INTERNAL_LINKS.COMPLIANCE_WORKLIST),
                  },
                  {
                    label: t(complianceDetailsQuery?.data?.type),
                  },
                  {
                    label: (
                      <Row align="middle" gutter={10} style={{ display: 'inline-flex' }}>
                        <Col>{getUserFullName(complianceDetailsQuery?.data?.userId)}</Col>
                        {complianceDetailsQuery?.data?.status && (
                          <Col>
                            <StatusTag status={complianceDetailsQuery?.data?.status} />
                          </Col>
                        )}
                      </Row>
                    ),
                  },
                ]
          }
        />
      }
      sideActionComponent={
        <Row gutter={[16, 16]}>
          <Col flex={1}>
            <Button block size="sm" onClick={openRemarksForUserModal}>
              {t('remarksForUser')}
            </Button>
          </Col>
          <Col flex={1}>
            <Button block size="sm" onClick={openRemarksForKliksAdminModal}>
              {t('remarksForKliksInternalAdmin')}
            </Button>
          </Col>
          {complianceDetailsQuery?.data?.userId?._id && (
            <Col flex={1}>
              <Button
                block
                size="sm"
                loading={impersonateMutation.isLoading}
                disabled={impersonateMutation.isLoading}
                onClick={impersonateMutation.mutateAsync}
              >
                {t('impersonateUser')}
              </Button>
            </Col>
          )}
        </Row>
      }
    >
      <PageHelmet />

      <Space direction="vertical" size="large">
        {isAxleInsurance && (
          <Row justify="start" align="bottom">
            <Col>
              <AxleConnectionStatus
                t={t}
                asDescription
                axleConnectionInfo={vehicleInsurance?.connection}
                policyRefreshedAt={vehicleInsurance?.updatedAt}
              />
            </Col>
          </Row>
        )}

        <Descriptions
          size="small"
          column={1}
          contentStyle={{ alignItems: 'center' }}
          labelStyle={{ alignItems: 'center' }}
        >
          {isInsuranceCompliance && insuranceProvider && (
            <Descriptions.Item
              label={
                <Text variant="b" size="large" renderAs="p">
                  {t('insuranceProvider')}
                </Text>
              }
            >
              <Text size="large">{t(`vehicleInsuranceProvider.${insuranceProvider}`)}</Text>
            </Descriptions.Item>
          )}

          {isAxleInsurance && vehicleInsurance?.externalPolicyId && (
            <Descriptions.Item
              label={
                <Text variant="b" size="large" renderAs="p">
                  {t('axlePolicyD')}
                </Text>
              }
            >
              <Text>
                <Typography.Paragraph
                  style={{ marginBottom: 0 }}
                  copyable={{
                    text: vehicleInsurance.externalPolicyId,
                    tooltips: [t('copy'), t('copied')],
                  }}
                >
                  {vehicleInsurance.externalPolicyId}
                </Typography.Paragraph>
              </Text>
            </Descriptions.Item>
          )}

          {isAxleInsurance && vehicleInsurance?.updatedAt && (
            <Descriptions.Item
              label={
                <Text variant="b" size="large" renderAs="p">
                  {t('lastRefreshed')}
                </Text>
              }
            >
              <Text size="large">
                {getMomentDateWithoutTimezone(vehicleInsurance.updatedAt).format(
                  'MMM DD, YYYY - h:mm A',
                )}
              </Text>
            </Descriptions.Item>
          )}

          {isAxleInsurance && vehicleInsurance?.modifiedAt && (
            <Descriptions.Item
              label={
                <Text variant="b" size="large" renderAs="p">
                  {t('lastModified')}
                </Text>
              }
            >
              <Text size="large">
                {getMomentDateWithoutTimezone(vehicleInsurance.modifiedAt).format(
                  'MMM DD, YYYY - h:mm A',
                )}
              </Text>
            </Descriptions.Item>
          )}
        </Descriptions>

        <Descriptions
          size="small"
          column={1}
          contentStyle={{ alignItems: 'center' }}
          labelStyle={{ alignItems: 'center' }}
        >
          <Descriptions.Item
            label={
              <Text variant="b" size="large" renderAs="p">
                {t('rate')}
              </Text>
            }
          >
            <Text size="large">
              {get(complianceDetailsQuery.data, 'userId.group.productId.name')}
            </Text>
          </Descriptions.Item>
        </Descriptions>

        {isAxleInsurance && (
          <Row gutter={[12, 12]}>
            <Col>
              <Button
                size="sm"
                loading={isOpeningAxleReport}
                onClick={handleOpenAxlePolicyReport}
                disabled={!vehicleInsurance?.externalPolicyId || isOpeningAxleReport}
              >
                {t('viewAxlePolicyReport')}
              </Button>
            </Col>
            <Col>
              <Button
                size="sm"
                onClick={handleOpenAxleDashboard}
                disabled={
                  !vehicleInsurance?.externalPolicyId ||
                  !get(
                    vehicleInsurance,
                    'source.externalAccountId.axleUserMappingId.axleClientId.destinationClientId',
                  )
                }
              >
                {t('viewAxleDashboard')}
              </Button>
            </Col>
          </Row>
        )}

        {!complianceDetailsQuery.isFetched && complianceDetailsQuery.isFetching && <SpaceSpinner />}

        {complianceDetailsQuery.isFetched && isBusinessVehicleCompliance && (
          <RegularInsuranceWorklistForm
            t={t}
            userId={get(complianceDetailsQuery.data, 'userId.id')}
            isLoading={complianceDetailsQuery.isFetching}
            formInstance={form}
            complianceStatus={complianceDetailsQuery?.data?.status}
            isInsuranceCompliance={isInsuranceCompliance}
            isBusinessVehicleCompliance={isBusinessVehicleCompliance}
            vehicleData={vehicleData}
            onVehicleDataChange={handleVehicleDataChange}
            onSubmit={handleSubmit}
            disabled={updateFavrComplianceMutation.isLoading}
          />
        )}

        {complianceDetailsQuery.isFetched && isInsuranceCompliance && !hasVehicleInsurance && (
          <Box>
            <Empty
              description={
                <Space direction="vertical" size="small">
                  <Text variant="b">{t('noSelectedInsurancePolicy')}</Text>
                  <Text>{t('noSelectedInsurancePolicyDescription')}</Text>
                </Space>
              }
            />
          </Box>
        )}

        {complianceDetailsQuery.isFetched && isInsuranceCompliance && hasVehicleInsurance && (
          <AxleInsuranceWorklistComparison
            t={t}
            isAxle={isAxleInsurance}
            provider={insuranceProvider}
            formInstance={form}
            disabled={overrideFavrComplianceComponentsMutation.isLoading}
            onSubmit={handleOverrideSubmit}
            complianceStatus={complianceDetailsQuery?.data?.status}
            user={get(complianceDetailsQuery.data, 'userId')}
            vehicle={get(complianceDetailsQuery.data, 'vehicleId')}
            insurancePolicy={vehicleInsurance}
            validationSummary={get(complianceDetailsQuery.data, 'validationSummary')}
            complianceComponents={get(complianceDetailsQuery.data, 'complianceComponents', {})}
            complianceComparisonResults={get(
              complianceDetailsQuery.data,
              'complianceComparisonResults',
            )}
            previousComplianceComponents={get(
              complianceDetailsQuery.data,
              'previousComplianceComponents',
            )}
          />
        )}

        {(isManualInsurance || isAxleInsurance) && uploadedFiles.length > 0 && (
          <Box>
            <Text variant="h5">{t('insurancePolicyDocument')}</Text>
            <UploadedFilesTable
              t={t}
              dataSource={uploadedFiles}
              pagination={{
                pageSize: 5,
                hideOnSinglePage: true,
                showSizeChanger: false,
              }}
              actions={{
                canDownload: true,
                download: setUploadedFileToView,
              }}
            />
          </Box>
        )}

        <Box>
          <Text variant="h5">{t('auditLog')}</Text>
          <ComplianceAuditLogTable
            t={t}
            showSearchInput={false}
            loading={userComplianceHistoryQuery.isFetching}
            dataSource={userComplianceHistoryQuery.data.documents}
            onChange={({ current }) => handlePageChange(current)}
            pagination={{
              pageSize: paginationConfig.pageSize,
              total: paginationConfig.total,
              current: paginationConfig.current,
              onShowSizeChange: handlePageSizeChange,
            }}
          />
        </Box>
      </Space>

      <GenericModal
        centered
        footer={null}
        bodyStyle={{ height: '100%' }}
        width={axleReportModalWidth}
        height={axleReportModalHeight}
        title={t('insuranceDocumentPreview')}
        visible={!!axleReport}
        onCancel={closeAxlePolicyReport}
      >
        {axleReport &&
          (isAxleReportImage ? (
            <img
              alt={t('insuranceDocumentPreview')}
              src={axleReport.url}
              style={{
                display: 'block',
                maxHeight: '100%',
                maxWidth: '100%',
                objectFit: 'contain',
              }}
            />
          ) : (
            <iframe
              frameBorder="0"
              height="99%"
              width="100%"
              title="Axle insurance document"
              src={axleReport.url}
            />
          ))}
      </GenericModal>

      <UploadedFilePreviewModal
        t={t}
        visible={!!uploadedFileToView}
        onCancel={() => setUploadedFileToView()}
        vehicleInsuranceId={vehicleInsurance?._id}
        uploadedFileId={uploadedFileToView?._id}
        filename={uploadedFileToView?.name}
      />

      <TripsCommentsModal
        t={t}
        canAddComment
        title={t('remarksForUser')}
        visible={isRemarksForUserModalVisible}
        onCancel={closeRemarksForUserModal}
        comments={complianceDetailsQuery?.data?.notesForUser || []}
        isAddingComment={addRemarksForUser.isLoading}
        onAddComment={note => addRemarksForUser.mutateAsync(note)}
      />

      <TripsCommentsModal
        t={t}
        canAddComment
        title={t('remarksForKliksInternalAdmin')}
        visible={isRemarksForKliksAdminModalVisible}
        onCancel={closeRemarksForKliksAdminModal}
        comments={complianceDetailsQuery?.data?.notes || []}
        isAddingComment={addRemarksForInternalAdmin.isLoading}
        onAddComment={note => addRemarksForInternalAdmin.mutateAsync(note)}
      />
    </PageContainer>
  );
};

export default withNamespaces()(withAuthentication(ComplianceWorklistDetails));
